import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr, fmtDate, downloadFile, COMPANY_STYLES } from "@/api";
import { useAuth } from "@/AuthContext";
import { useCompany } from "@/store";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FileDown, ShieldAlert, IndianRupee, Link2, FileText } from "lucide-react";

const PAY_MODES = ["Cash", "UPI", "Card", "Bank", "Cheque"];

const P_STYLE = {
  Due: "bg-danger-tint text-danger border-danger/30",
  Partial: "bg-warning-tint text-warning border-warning/30",
  Full: "bg-success-tint text-success border-success/30",
};

export default function Billing() {
  const { user } = useAuth();
  const [bills, setBills] = useState([]);
  const [billable, setBillable] = useState([]);
  const [createFor, setCreateFor] = useState(null);
  const [form, setForm] = useState({ bill_amount: "", payment_status: "Due", paid_amount: 0 });
  const [tab, setTab] = useState("All");
  const { company: companyCtx } = useCompany();
  const [overrideFor, setOverrideFor] = useState(null);
  const [ovr, setOvr] = useState({ bill_amount: "", gst_amount: "", commission_amount: "", travel_allowance: "", reason: "" });
  const [payFor, setPayFor] = useState(null);
  const [pay, setPay] = useState({ amount: "", mode: "UPI", note: "" });
  const [reports, setReports] = useState(null);
  const [showReports, setShowReports] = useState(false);

  const recordPayment = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/payments", { bill_id: payFor.id, amount: parseFloat(pay.amount), mode: pay.mode, note: pay.note });
      toast.success(`Receipt ${r.data.receipt_no} — invoice ${r.data.amount >= payFor.total_with_gst - (payFor.paid_amount || 0) ? "fully paid" : "partially paid"}`);
      setPayFor(null); setPay({ amount: "", mode: "UPI", note: "" }); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const payLink = async (b) => {
    try {
      const r = await api.post(`/bills/${b.id}/payment-link`);
      await navigator.clipboard.writeText(r.data.url);
      toast.success(r.data.mock ? "Payment link copied (mock Razorpay)" : "Payment link copied");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const loadReports = async () => {
    if (showReports) { setShowReports(false); return; }
    try {
      const [pnl, aging, gst, daybook] = await Promise.all([
        api.get("/reports/pnl"), api.get("/reports/aging"), api.get("/reports/gst"), api.get("/reports/daybook")]);
      setReports({ pnl: pnl.data, aging: aging.data, gst: gst.data, daybook: daybook.data });
      setShowReports(true);
    } catch { toast.error("Reports failed"); }
  };

  const submitOverride = async (e) => {
    e.preventDefault();
    try {
      const payload = { reason: ovr.reason };
      for (const k of ["bill_amount", "gst_amount", "commission_amount", "travel_allowance"]) {
        if (ovr[k] !== "") payload[k] = parseFloat(ovr[k]);
      }
      await api.post(`/bills/${overrideFor.id}/override`, payload);
      toast.success("Invoice corrected & logged");
      setOverrideFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const load = useCallback(async () => {
    const params = companyCtx !== "all" ? { company: companyCtx } : {};
    const [b, completed] = await Promise.all([api.get("/bills", { params }), api.get("/jobs?status=Completed")]);
    setBills(b.data);
    setBillable(completed.data.filter((j) => !b.data.find((x) => x.job_id === j.id)));
  }, [companyCtx]);
  useEffect(() => { load(); }, [load]);

  const createBill = async (e) => {
    e.preventDefault();
    try {
      await api.post("/bills", { job_id: createFor.id, bill_amount: parseFloat(form.bill_amount), payment_status: form.payment_status, paid_amount: form.payment_status === "Full" ? parseFloat(form.bill_amount) : parseFloat(form.paid_amount || 0) });
      toast.success("Invoice generated with GST & commission");
      setCreateFor(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const markPaid = async (b) => {
    try {
      await api.patch(`/bills/${b.id}`, { payment_status: "Full", paid_amount: b.total_with_gst });
      toast.success("Marked fully paid"); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const filtered = tab === "All" ? bills : bills.filter((b) => b.payment_status === tab);
  const summary = {
    total: bills.length,
    due: bills.filter((b) => b.payment_status === "Due").length,
    partial: bills.filter((b) => b.payment_status === "Partial").length,
    collected: bills.filter((b) => b.payment_status === "Full").length,
    outstanding: bills.reduce((sum, b) => sum + ((b.total_with_gst || 0) - (b.paid_amount || 0)), 0),
  };

  return (
    <div className="space-y-5" data-testid="billing-page">
      <div className="rounded-[28px] border border-border/70 bg-surface-elevated p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.22em] text-primary">Finance</p>
            <h1 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Billing & Multi-Brand GST</h1>
            <p className="mt-1 text-sm text-muted-foreground">Auto GST, commission & net earning per company — Cleanmates, PestGenis, StainGuard Pro, Rover, Limpio</p>
          </div>
          <Button variant="outline" data-testid="finance-reports-toggle" onClick={loadReports} className="h-11 rounded-xl border-border/80 bg-background/70 hover:bg-accent">
            <FileText size={15} className="mr-1.5" /> {showReports ? "Hide" : "Show"} Finance Reports
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        {[
          { label: "Invoices", value: summary.total, tone: "bg-info-tint text-info" },
          { label: "Due", value: summary.due, tone: "bg-danger-tint text-danger" },
          { label: "Partial", value: summary.partial, tone: "bg-warning-tint text-warning" },
          { label: "Collected", value: summary.collected, tone: "bg-success-tint text-success" },
          { label: "Outstanding", value: inr(summary.outstanding), tone: "bg-primary-tint text-primary" },
        ].map((item) => (
          <Card key={item.label} className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{item.label}</p>
                <div className="mt-3 text-2xl font-bold tabular-nums">{item.value}</div>
              </div>
              <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${item.tone}`}>
                <span className="h-2.5 w-2.5 rounded-full bg-current opacity-80" />
              </span>
            </div>
          </Card>
        ))}
      </div>

      {showReports && reports && (
        <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="finance-reports">
          <Card className="p-4 rounded-2xl" data-testid="report-pnl">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">P&L — {reports.pnl.month}</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Revenue</span><b className="font-mono">{inr(reports.pnl.revenue)}</b></div>
              <div className="flex justify-between"><span>Expenses</span><b className="font-mono text-danger-foreground">−{inr(reports.pnl.expenses)}</b></div>
              <div className="flex justify-between border-t pt-1"><span>Gross Profit</span><b className={`font-mono ${reports.pnl.gross_profit >= 0 ? "text-success" : "text-danger"}`}>{inr(reports.pnl.gross_profit)}</b></div>
            </div>
          </Card>
          <Card className="p-4 rounded-2xl" data-testid="report-aging">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Receivables Aging</div>
            <div className="text-sm space-y-1">
              {Object.entries(reports.aging.buckets).map(([k, v]) => (
                <div key={k} className="flex justify-between"><span>{k} days</span><b className="font-mono">{inr(v)}</b></div>
              ))}
            </div>
          </Card>
          <Card className="p-4 rounded-2xl" data-testid="report-gst">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">GST Sales Register — {reports.gst.month}</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Taxable</span><b className="font-mono">{inr(reports.gst.totals.taxable)}</b></div>
              <div className="flex justify-between"><span>GST (CGST+SGST)</span><b className="font-mono">{inr(reports.gst.totals.gst)}</b></div>
              <div className="flex justify-between"><span>Invoices</span><b className="font-mono">{reports.gst.rows.length}</b></div>
            </div>
          </Card>
          <Card className="p-4 rounded-2xl" data-testid="report-daybook">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Day Book — {reports.daybook.date}</div>
            <div className="text-sm space-y-1">
              <div className="flex justify-between"><span>Receipts</span><b className="font-mono text-success">+{inr(reports.daybook.inflow)}</b></div>
              <div className="flex justify-between"><span>Expenses</span><b className="font-mono text-danger-foreground">−{inr(reports.daybook.outflow)}</b></div>
              <div className="flex justify-between border-t pt-1"><span>Net</span><b className="font-mono">{inr(reports.daybook.net)}</b></div>
            </div>
          </Card>
        </div>
      )}

      {billable.length > 0 && (
        <Card className="p-5" data-testid="billable-jobs-card">
          <h3 className="font-heading font-semibold mb-3">Completed Jobs Awaiting Invoice ({billable.length})</h3>
          <div className="space-y-2">
            {billable.map((j) => (
              <div key={j.id} className="flex items-center gap-3 border border-border/70 rounded-xl bg-muted/30 p-3 text-sm flex-wrap">
                <span className="font-mono text-xs">{j.job_no}</span>
                <span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[j.company] || ""}`}>{j.company}</span>
                <span className="flex-1">{j.customer_name} — {j.service}</span>
                <b className="font-mono">{inr(j.amount)}</b>
                <Button size="sm" data-testid={`create-bill-${j.id}`} onClick={() => { setCreateFor(j); setForm({ bill_amount: j.amount, payment_status: "Due", paid_amount: 0 }); }} className="h-8 bg-primary text-primary-foreground hover:bg-primary/90">Create Invoice</Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-2 sm:p-3">
        <div className="flex gap-2 flex-wrap">
          {["All", "Due", "Partial", "Full"].map((s) => (
            <button key={s} data-testid={`bill-tab-${s.toLowerCase()}`} onClick={() => setTab(s)}
              className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-all ${tab === s ? "border-primary bg-primary text-primary-foreground shadow-sm" : "border-transparent bg-muted/60 text-foreground hover:bg-muted"}`}>
              {s}
              {s !== "All" && <span className="rounded-full bg-primary-tint/10 px-1.5 py-0.5 text-[10px] opacity-80">{bills.filter((b) => b.payment_status === s).length}</span>}
            </button>
          ))}
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm min-w-[900px]" data-testid="bills-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <th className="px-4 py-3">Invoice</th><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Brand</th>
              <th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">GST</th>
              <th className="px-4 py-3 text-right">Commission</th><th className="px-4 py-3 text-right">Net</th>
              <th className="px-4 py-3 text-right">Total</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => (
              <tr key={b.id} data-testid={`bill-row-${b.id}`} className="border-t border-border/70 transition-colors hover:bg-accent/40">
                <td className="px-4 py-3 font-mono text-xs">{b.invoice_no}<div className="text-muted-foreground">{fmtDate(b.created_at)}</div></td>
                <td className="px-4 py-3">{b.customer_name}<div className="text-xs text-muted-foreground">{b.job_no}</div></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${COMPANY_STYLES[b.company] || ""}`}>{b.company}</span></td>
                <td className="px-4 py-3 text-right font-mono">{inr(b.bill_amount)}</td>
                <td className="px-4 py-3 text-right font-mono text-xs">{inr(b.gst_amount)}<div className="text-muted-foreground">@{b.gst_rate}%</div></td>
                <td className="px-4 py-3 text-right font-mono text-xs">{inr(b.commission_amount)}<div className="text-muted-foreground">@{b.commission_pct}%</div></td>
                <td className="px-4 py-3 text-right font-mono">{inr(b.net_earning)}</td>
                <td className="px-4 py-3 text-right font-mono font-semibold">{inr(b.total_with_gst)}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full border ${P_STYLE[b.payment_status] || ""}`}>{b.payment_status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 justify-end">
                    {b.payment_status !== "Full" && (
                      <Button size="sm" data-testid={`bill-pay-${b.id}`} onClick={() => { setPayFor(b); setPay({ amount: String(b.total_with_gst - (b.paid_amount || 0)), mode: "UPI", note: "" }); }} className="h-8 bg-success text-inverse hover:bg-success/90"><IndianRupee size={13} className="mr-1" /> Receive</Button>
                    )}
                    {b.payment_status !== "Full" && <Button size="sm" variant="outline" data-testid={`bill-link-${b.id}`} onClick={() => payLink(b)} className="h-8" title="Copy payment link"><Link2 size={13} /></Button>}
                    {b.payment_status !== "Full" && <Button size="sm" variant="outline" data-testid={`bill-paid-${b.id}`} onClick={() => markPaid(b)} className="h-8">Mark Paid</Button>}
                    {user.role === "super_admin" && (
                      <Button size="sm" variant="outline" data-testid={`bill-override-${b.id}`} title="Owner override"
                        onClick={() => { setOverrideFor(b); setOvr({ bill_amount: b.bill_amount, gst_amount: b.gst_amount, commission_amount: b.commission_amount, travel_allowance: b.travel_allowance, reason: "" }); }}
                        className="h-8 text-warning-foreground border-warning"><ShieldAlert size={13} /></Button>
                    )}
                    <Button size="sm" variant="outline" data-testid={`bill-pdf-${b.id}`} onClick={() => downloadFile(`/bills/${b.id}/pdf`, `${b.invoice_no}.pdf`).catch(() => toast.error("PDF failed"))} className="h-8"><FileDown size={14} /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">No invoices</td></tr>}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!createFor} onOpenChange={() => setCreateFor(null)}>
        <DialogContent data-testid="create-bill-dialog">
          <DialogHeader><DialogTitle className="font-heading">Invoice — {createFor?.job_no}</DialogTitle></DialogHeader>
          <form onSubmit={createBill} className="space-y-3">
            <div className="text-sm text-muted-foreground">{createFor?.customer_name} · {createFor?.service} · <b className="text-foreground">{createFor?.company}</b></div>
            <div>
              <label className="text-sm font-medium">Bill Amount (₹, excl. GST)</label>
              <Input data-testid="bill-amount-input" type="number" min="1" step="0.01" value={form.bill_amount} onChange={(e) => setForm({ ...form, bill_amount: e.target.value })} required className="mt-1.5" />
            </div>
            <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
              <SelectTrigger data-testid="bill-status-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Due">Due</SelectItem><SelectItem value="Partial">Partial</SelectItem><SelectItem value="Full">Full</SelectItem></SelectContent>
            </Select>
            {form.payment_status === "Partial" && (
              <Input data-testid="bill-paid-input" type="number" min="0" step="0.01" placeholder="Amount received (₹)" value={form.paid_amount} onChange={(e) => setForm({ ...form, paid_amount: e.target.value })} />
            )}
            <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3">GST, commission & travel allowance are auto-calculated from the {createFor?.company} rate card (Settings).</div>
            <Button data-testid="bill-save-button" type="submit" className="w-full bg-primary hover:bg-primary-hover">Generate Invoice</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!payFor} onOpenChange={() => setPayFor(null)}>
        <DialogContent data-testid="payment-dialog" aria-describedby="payment-desc">
          <DialogHeader>
            <DialogTitle className="font-heading">Receive Payment — {payFor?.invoice_no}</DialogTitle>
            <DialogDescription id="payment-desc">Record a receipt against this invoice; status updates automatically.</DialogDescription>
          </DialogHeader>
          <form onSubmit={recordPayment} className="space-y-3">
            <div className="text-sm text-muted-foreground">{payFor?.customer_name} · Outstanding <b className="text-foreground">{inr((payFor?.total_with_gst || 0) - (payFor?.paid_amount || 0))}</b></div>
            <div>
              <label className="text-sm font-medium">Amount (₹)</label>
              <Input data-testid="payment-amount-input" type="number" min="1" step="0.01" value={pay.amount} onChange={(e) => setPay({ ...pay, amount: e.target.value })} required className="mt-1.5" />
            </div>
            <Select value={pay.mode} onValueChange={(v) => setPay({ ...pay, mode: v })}>
              <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>{PAY_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid="payment-note-input" placeholder="Note (optional)" value={pay.note} onChange={(e) => setPay({ ...pay, note: e.target.value })} />
            <Button data-testid="payment-save-button" type="submit" className="w-full bg-success text-inverse hover:bg-success/90">Record Payment & Receipt</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!overrideFor} onOpenChange={() => setOverrideFor(null)}>
        <DialogContent data-testid="override-dialog" aria-describedby="override-desc">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ShieldAlert size={17} className="text-warning-foreground" /> Owner Override — {overrideFor?.invoice_no}</DialogTitle>
            <DialogDescription id="override-desc">Correct financial fields; a reason is required and written to the audit log.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitOverride} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {[["bill_amount", "Bill Amount"], ["gst_amount", "GST Amount"], ["commission_amount", "Commission"], ["travel_allowance", "Travel Allowance"]].map(([k, label]) => (
                <div key={k}>
                  <label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</label>
                  <Input data-testid={`override-${k}`} type="number" step="0.01" value={ovr[k]} onChange={(e) => setOvr({ ...ovr, [k]: e.target.value })} className="mt-1" />
                </div>
              ))}
            </div>
            <div>
              <label className="text-xs uppercase tracking-wide text-muted-foreground">Reason (required, logged)</label>
              <Input data-testid="override-reason" value={ovr.reason} onChange={(e) => setOvr({ ...ovr, reason: e.target.value })} required className="mt-1" placeholder="e.g. GST rate correction after CA review" />
            </div>
            <Button data-testid="override-save-button" type="submit" className="w-full bg-warning hover:bg-warning">Apply Correction</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
